import omitProps from '@hocs/omit-props';
import withLifecycle from '@hocs/with-lifecycle';
import { events } from '@wix/media-manager-sdk';
import { array, bool, string } from 'prop-types';
import { allPass, assoc, equals, filter, isEmpty, map, pathOr, propEq } from 'ramda';
import { contained, isTruthy } from 'ramda-adjunct';
import { injectIntl } from 'react-intl';
import { compose, mapProps, setDisplayName, setPropTypes, withHandlers, withPropsOnChange } from 'recompose';
import { Observable, Subscription } from 'rxjs';
import { ItemType } from '../../../constants/ItemType';
import { ItemSelectOrigin } from '../../hooks/bi/useTrackItemSelectAnalytics/types';
import { MediaManagerSDKAnyItem, MediaManagerSDKResponse } from '../../media-manager-sdk';
import { getTranslatedItemName } from '../../media-manager-sdk/items';
import { withItemSelectAnalytics } from '../bi/withItemSelectAnalytics';
import { ejectIntl } from '../ejectIntl';
import { onPropChange } from '../onPropChange';
import { reducerHOC } from './withItemsReducer.reducer';
import { getItem, getItemIds, getItems, getSelectedItems } from './withItemsReducer.selectors';
import { IItemsReducer, ItemsAction, IWithItemsReducerInner, IWithItemsReducerOuter } from './withItemsReducer.types';

const itemUpdateObservable = new Observable<MediaManagerSDKAnyItem>((observer) => {
  type THandler = (response: MediaManagerSDKResponse<MediaManagerSDKAnyItem>) => void;
  const handler: THandler = (promise) => {
    promise.then((response) => {
      observer.next(response.data);
    });
  };

  events.on(events.ITEM.UPDATE, handler);

  return () => {
    events.off(events.ITEM.UPDATE, handler);
  };
});

const translateItems = (items, formatMessage) =>
  map<MediaManagerSDKAnyItem, MediaManagerSDKAnyItem>((item) => {
    const translatedName = getTranslatedItemName(item, formatMessage);
    if (translatedName !== item.name) {
      return assoc('name', translatedName, item);
    }
    return item;
  }, items);

export const withItemsReducer = compose<IWithItemsReducerInner, IWithItemsReducerOuter>(
  setPropTypes({
    fileType: string.isRequired,
    items: array.isRequired,
    nextPageItems: array.isRequired,
    isLoading: bool.isRequired,
  }),
  setDisplayName('withItemsReducer'),
  reducerHOC,
  withItemSelectAnalytics,
  onPropChange(
    ['isLoading', 'items'],
    ({ isLoading, modifyItemsState, items }) => {
      if (!isLoading) {
        // loading finished - need to re-init items reducer
        modifyItemsState({
          type: ItemsAction.Reset,
          payload: { items, isLoading: false },
        });
      } else {
        modifyItemsState({
          type: ItemsAction.Reset,
          payload: { items, isLoading: true },
        });
      }
    },
    true,
  ),
  mapProps<Partial<IItemsReducer>, any>(({ itemsState, ...props }) => {
    const { editingItem } = itemsState;

    const itemIds = getItemIds(itemsState);
    const items = getItems(itemsState);
    const selectedItems = getSelectedItems(itemsState);
    const selectedItemsCount = selectedItems.length;

    return {
      itemsState,
      ...props,
      itemIds,
      items,
      isLoading: itemsState.isLoading,
      editingItem,
      selectedItems,
      selectedItemsCount,
      isSingleItemSelected: selectedItemsCount === 1,
      areMultipleItemsSelected: selectedItemsCount > 1,
      hasSelectedItems: selectedItemsCount > 0,
      hasNoItems: !itemsState.isLoading && !items.length,
    };
  }),
  injectIntl,
  withPropsOnChange(['items'], ({ items, intl: { formatMessage } }) => ({
    items: translateItems(items, formatMessage),
  })),
  withPropsOnChange(['selectedItems'], ({ selectedItems, intl: { formatMessage } }) => ({
    selectedItems: translateItems(selectedItems, formatMessage),
  })),
  ejectIntl,
  withHandlers({
    prependItems:
      ({ modifyItemsState }) =>
      (items) =>
        modifyItemsState({
          type: ItemsAction.PrependItems,
          payload: { items },
        }),
    insertAfterFolders:
      ({ modifyItemsState }) =>
      (items) =>
        modifyItemsState({
          type: ItemsAction.InsertAfterFolders,
          payload: { items },
        }),
    appendItems:
      ({ modifyItemsState }) =>
      (items) =>
        modifyItemsState({
          type: ItemsAction.AppendItems,
          payload: { items },
        }),
    removeItems:
      ({ modifyItemsState }) =>
      (items, shouldSelectNextItem = false) =>
        modifyItemsState({
          type: ItemsAction.RemoveItems,
          payload: { items, shouldSelectNextItem },
        }),
    clearAll:
      ({ modifyItemsState }) =>
      () =>
        modifyItemsState({
          type: ItemsAction.ClearAll,
        }),
    clearItemsSelection:
      ({ modifyItemsState }) =>
      () =>
        modifyItemsState({ type: ItemsAction.ClearItemsSelection }),
    selectItem:
      ({ modifyItemsState, itemsState, trackItemsSelected }) =>
      (itemId, { clickOrigin = ItemSelectOrigin.Click, ...params } = {}) => {
        const item = getItem(itemsState)(itemId);

        if (!item) {
          return;
        }

        trackItemsSelected([item], { ...params, clickOrigin });
        modifyItemsState({
          type: ItemsAction.SelectItems,
          payload: { itemIds: [itemId] },
        });
      },
    selectAllItems:
      ({ modifyItemsState, itemIds, items, trackItemsSelected }) =>
      () => {
        trackItemsSelected(items, { clickOrigin: ItemSelectOrigin.CtrlA });
        modifyItemsState({
          type: ItemsAction.SelectItems,
          payload: { itemIds },
        });
      },
    selectItemsUntil:
      ({ modifyItemsState }) =>
      (itemId) =>
        modifyItemsState({
          type: ItemsAction.SelectItemsUntil,
          payload: { itemId },
        }),
    selectItems:
      ({ modifyItemsState }) =>
      (itemIds) => {
        modifyItemsState({
          type: ItemsAction.SelectItems,
          payload: { itemIds },
        });
      },
    addItemToSelection:
      ({ modifyItemsState, itemsState, trackItemsSelected }) =>
      (itemId, { clickOrigin = ItemSelectOrigin.Click, ...params }) => {
        const item = getItem(itemsState)(itemId);
        trackItemsSelected([item], { ...params, clickOrigin });
        modifyItemsState({
          type: ItemsAction.AddItemToSelection,
          payload: { itemId },
        });
      },
    removeItemFromSelection:
      ({ modifyItemsState }) =>
      (itemId) =>
        modifyItemsState({
          type: ItemsAction.RemoveItemFromSelection,
          payload: { itemId },
        }),
    editItemStart:
      ({ modifyItemsState }) =>
      (itemId) =>
        modifyItemsState({
          type: ItemsAction.EditItemStart,
          payload: { itemId },
        }),
    editItemComplete:
      ({ modifyItemsState }) =>
      () =>
        modifyItemsState({
          type: ItemsAction.EditItemComplete,
        }),
    editItemCancel:
      ({ modifyItemsState }) =>
      () =>
        modifyItemsState({
          type: ItemsAction.EditItemCancel,
        }),
    updateEditingItem:
      ({ modifyItemsState }) =>
      (updatedItem) =>
        modifyItemsState({
          type: ItemsAction.UpdateEditingItem,
          payload: { updatedItem },
        }),
    updateLocalItem:
      ({ modifyItemsState }) =>
      (itemId, updatedItem) =>
        modifyItemsState({
          type: ItemsAction.UpdateLocalItem,
          payload: { itemId, updatedItem },
        }),
    jumpToNextItem:
      ({ modifyItemsState }) =>
      ({ withShift, jumpTo }) =>
        modifyItemsState({
          type: ItemsAction.JumpToNextItem,
          payload: { withShift, jumpTo },
        }),
    createNewFolderStart:
      ({ modifyItemsState }) =>
      (id) =>
        modifyItemsState({
          type: ItemsAction.CreateNewFolderStart,
          payload: { newFolderId: id },
        }),
    createNewFolderCancel:
      ({ modifyItemsState }) =>
      () =>
        modifyItemsState({
          type: ItemsAction.CreateNewFolderCancel,
        }),
    createNewFolderComplete:
      ({ modifyItemsState }) =>
      (newFolder) =>
        modifyItemsState({
          type: ItemsAction.CreateNewFolderComplete,
          payload: { updatedItem: newFolder },
        }),
    isItemSelected: ({ itemsState }) => contained(itemsState.selectedItems),
    isItemEditing: ({ itemsState }) => allPass([isTruthy, equals(pathOr(null, ['editingItem', 'id'], itemsState))]),
    preventActionIfItemEditing:
      ({ editingItem }) =>
      (action) =>
      (...args) => {
        if (!editingItem) {
          action(...args);
        }
      },
  }),
  withHandlers({
    toggleItemSelection:
      ({ isItemSelected, addItemToSelection, removeItemFromSelection }) =>
      (itemId, params = {}) => {
        const method = isItemSelected(itemId) ? removeItemFromSelection : addItemToSelection;
        method(itemId, params);
      },
    removeAllFiles:
      ({ removeItems, items }) =>
      () => {
        removeItems(filter(propEq('kind', ItemType.File), items));
      },
    updateLocalItemEditedOutside:
      ({ updateLocalItem }) =>
      (item: MediaManagerSDKAnyItem) => {
        updateLocalItem(item.id, item);
      },
  }),
  withLifecycle(() => {
    let resetFilesStreamSubscription: Subscription = null;
    let itemUpdateSubscription: Subscription = null;

    return {
      onDidMount({ resetFilesStream, removeAllFiles, updateLocalItemEditedOutside }) {
        if (resetFilesStream) {
          resetFilesStreamSubscription = resetFilesStream.subscribe(removeAllFiles);
        }

        itemUpdateSubscription = itemUpdateObservable.subscribe(updateLocalItemEditedOutside);
      },
      onWillUnmount() {
        if (resetFilesStreamSubscription) {
          resetFilesStreamSubscription.unsubscribe();
        }

        if (itemUpdateSubscription) {
          itemUpdateSubscription.unsubscribe();
        }
      },
      onDidUpdate({ nextPageItems: prevNextPageItems }, { nextPageItems, appendItems }) {
        if (
          prevNextPageItems &&
          nextPageItems &&
          !equals(prevNextPageItems, nextPageItems) &&
          !isEmpty(nextPageItems)
        ) {
          appendItems(nextPageItems);
        }
      },
    };
  }),
  omitProps('itemsState'),
);
